import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { AuthLayout, Button, Field, Notice, PasswordChecklist, PASSWORD_PROBLEM_TEXT } from "../components";
import { useRouter, Link } from "../router";
import { useSession } from "../session";

export function Invite({ token }: { token: string }) {
  const { navigate } = useRouter();
  const { signedIn } = useSession();
  const [inv, setInv] = useState<{ fullName: string; email: string | null; hasPassword: boolean } | null | "invalid">(null);
  const [pw, setPw] = useState(""), [pw2, setPw2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Keep the single-use token out of the address bar and browser history once it has been read.
    history.replaceState(null, "", "/invite");
    api.post("/api/v1/auth/invitations/peek", { token }).then(setInv).catch(() => setInv("invalid"));
  }, [token]);

  if (inv === null) return <AuthLayout title="Checking your invitation…"><p className="lede">One moment.</p></AuthLayout>;
  if (inv === "invalid") return (
    <AuthLayout title="This link has expired" subtitle="Invitation links work once and expire after 72 hours. Ask your administrator to send a new one.">
      <Link to="/login" className="btn btn-secondary">Go to sign in</Link>
    </AuthLayout>
  );

  const mismatch = !inv.hasPassword && pw2.length > 0 && pw !== pw2;
  async function submit(e: FormEvent) {
    e.preventDefault(); setError(null);
    if (!inv || inv === "invalid") return;
    if (!inv.hasPassword && pw !== pw2) { setError("The two passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/api/v1/auth/invitations/accept", { token, password: pw });
      const login = await api.post("/api/v1/auth/login", { identifier: inv.email, password: pw });
      if (login.status === "ok") navigate((await signedIn(login.accessToken, login.memberships)) === "choose" ? "/select-organisation" : "/app", true);
      else navigate("/login", true);
    } catch (err) {
      const ex = err as ApiError;
      setError(ex.code === "weak_password" ? (ex.data.problems as string[]).map((p) => PASSWORD_PROBLEM_TEXT[p] ?? p).join(" ") : ex.message);
    } finally { setBusy(false); }
  }

  return (
    <AuthLayout title={`Welcome, ${inv.fullName}`}
      subtitle={inv.hasPassword ? "You already have a HealthOS account. Enter your existing password to join this organisation." : "Choose a password to finish setting up your account."}>
      <form onSubmit={submit}>
        {error && <Notice tone="error">{error}</Notice>}
        <Field label="Email" value={inv.email ?? ""} readOnly autoComplete="username" />
        <Field label={inv.hasPassword ? "Your password" : "New password"} type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
          autoComplete={inv.hasPassword ? "current-password" : "new-password"} autoFocus />
        {!inv.hasPassword && <>
          <PasswordChecklist password={pw} name={inv.fullName} email={inv.email} />
          <Field label="Confirm password" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password"
            error={mismatch ? "The two passwords don't match." : undefined} />
        </>}
        <Button type="submit" busy={busy}>{inv.hasPassword ? "Join organisation" : "Set password and continue"}</Button>
      </form>
    </AuthLayout>
  );
}
